import React, { useMemo } from 'react';
import styled from 'styled-components';
import { EditorDeclarativeBlock } from '@/webapi/use-experience-api';
import { ChangeScreenshot } from '@/utils/change-screenshot';
import { CatalogApp } from '@/webapi/use-widget-catalog-api';
import { CheckoutWidgetChange, WidgetChange } from '@/pkg/sdk';
import { getOriginExperienceName } from '@/features/editor/shared/external_experience_widget';
import { SelectorSection } from '../components/selector-section';
import { SelectorTooltip } from '../components/selector-tooltip';
import { getWidgetDisplayName } from './title-utils';

interface WidgetActionTitleProps {
  prefix: string;
  directive: string;
  change: EditorDeclarativeBlock;
  screenshot: ChangeScreenshot;
  appsCatalog: CatalogApp[];
}

export function WidgetActionTitle({
  prefix,
  directive,
  change,
  screenshot,
  appsCatalog,
}: WidgetActionTitleProps) {
  const originExperienceName = getOriginExperienceName(change);
  const changeTooltipId = useMemo(() => `change-${change.editorId}`, [change]);

  if (originExperienceName) {
    return (
      <ExternalWidgetTitle>
        <span>
          {change?.hint && (
            <>
              {change?.hint} <br />
            </>
          )}
          Personalization of experience{` `}
          <b>
            <strong>{originExperienceName}</strong>
          </b>
        </span>
      </ExternalWidgetTitle>
    );
  }

  const widgetName = getWidgetDisplayName(appsCatalog, change);

  return (
    <>
      {!!change?.hint && (
        <>
          <span data-tip data-for={changeTooltipId}>
            {change.hint}
          </span>
          <SelectorTooltip
            id={changeTooltipId}
            selector={`${prefix} ${widgetName} ${directive} ${change.block.selector}`}
            image=""
          />
        </>
      )}
      {!change?.hint && (
        <>
          {change.block.kind !== `compound` && (
            <SelectorTooltip
              id={changeTooltipId}
              selector={
                (change?.block?.value as WidgetChange)?.env?.[`sectionId`] ||
                `#${
                  (change?.block?.value as CheckoutWidgetChange)?.sectionId ||
                  ``
                }`
              }
              image={screenshot.changeUrl}
            />
          )}
          <span data-tip data-for={changeTooltipId}>
            {prefix}
            {` `}
            <b>{widgetName}</b>
          </span>
          {` `}
          <Directive>{directive}</Directive>
          <SelectorSection
            image={screenshot.mountUrl || screenshot.changeUrl}
            editorId={change.editorId}
            selector={
              change.block.kind === `checkoutWidget`
                ? change?.checkoutMountPointInfo?.mountPointSelector
                : change.block.selector
            }
          />
        </>
      )}
    </>
  );
}

const Directive = styled.span`
  color: #8e969d;
  text-transform: lowercase;
  margin-right: 0.3rem;
`;

const ExternalWidgetTitle = styled.div``;
